import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

/* iter101.3 — sessionStorage-first token read matches the request
   interceptor in `@/lib/api`. When an admin impersonates a doctor /
   patient in a new tab, that tab's sessionStorage holds the target's
   JWT while the admin's original tab keeps its localStorage token —
   both AuthContext instances read the "right" one for their tab. */
function readAuthToken() {
    try {
        const s = sessionStorage.getItem("ca_token");
        if (s) return s;
    } catch { /* privacy mode */ }
    return localStorage.getItem("ca_token");
}

function clearAuthToken() {
    try { sessionStorage.removeItem("ca_token"); } catch { /* noop */ }
    localStorage.removeItem("ca_token");
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadUser = useCallback(async () => {
        const token = readAuthToken();
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            clearAuthToken();
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const login = (token, userData) => {
        localStorage.setItem("ca_token", token);
        // iter100.198 / iter100.198a — Reset ALL portal sidebar collapse
        // prefs on every fresh login. Otherwise a logout-then-relogin
        // cycle in the same tab would carry over the collapsed
        // sessionStorage flag. Each portal layout re-reads its own key
        // on mount, so clearing here + on logout is enough. See
        // PortalLayout.jsx / AdminLayout.jsx / CreatorShell.jsx for the
        // persistence contract.
        try {
            sessionStorage.removeItem("invq_doctor_sidebar_collapsed");
            sessionStorage.removeItem("invq_admin_sidebar_collapsed");
            sessionStorage.removeItem("invq_creator_shell_collapsed");
        } catch { /* noop */ }
        setUser(userData);
    };

    const logout = async () => {
        // 2026-02-28 — Atomic logout with uniform "→ /" destination across
        // all roles. The naive approach (clear state + navigate("/")) loses
        // a race against the protected-route guards on /doctor, /admin, /coe
        // which each fire <Navigate to="/{role}-login"> the moment user
        // becomes null. We coordinate via TWO sessionStorage flags:
        //
        //   isLoggingOut       — read by ProtectedRoute / AdminLayout /
        //                         CoeLayout to redirect to "/" instead of
        //                         their respective login pages. Cleared on
        //                         first read.
        //   skipPortalRedirect — read by PortalRedirect on "/" to NOT bounce
        //                         a freshly-logged-out user back into their
        //                         portal mid-redirect.
        //
        // Set these BEFORE clearing user state, so the very first re-render
        // after setUser(null) already sees the flags. Then state clear +
        // toast happen synchronously, and the API ping fires fire-and-forget.
        sessionStorage.setItem("isLoggingOut", "1");
        sessionStorage.setItem("skipPortalRedirect", "1");
        // iter100.198 / iter100.198a — Also drop ALL portal sidebar
        // collapse prefs so a fresh login (in this same tab) starts
        // from the viewport default again.
        try {
            sessionStorage.removeItem("invq_doctor_sidebar_collapsed");
            sessionStorage.removeItem("invq_admin_sidebar_collapsed");
            sessionStorage.removeItem("invq_creator_shell_collapsed");
        } catch { /* noop */ }
        clearAuthToken();
        setUser(null);
        toast.success("Signed out");
        try { await api.post("/auth/logout"); } catch { /* noop */ }
    };

    return (
        <AuthContext.Provider value={{ user, setUser, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
